import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import {
  AutoCompleteExtraProps,
  CompactEditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
} from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormEnums } from "models/enums";
import { DateConstants, FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { TimeReportingFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { WeeklyFormViewProps } from "models/propTypes/timeReportingPropTypes";
import { TimeReportType } from "models/enums/timeReporting";
//#endregion
//#region components
import { GenericForm, MoreActionsButton } from "components/common";
import IconField from "../../_partial/iconField";
import WeeklyFormTableHeader from "../weeklyFormTableHeader";
import ProjectCategoryPickerField from "../_partial/projectCategoryPickerField";
import CommentsCountField from "../_partial/commentsCountField";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const WeeklyForm: ForwardRefRenderFunction<FormHandles, WeeklyFormViewProps> = (props, ref) => {
  const {
    tableHeaderValues,
    availableProjects,
    onProjectSelect,
    availableDeviations,
    availableCategories,
    onDeviationSelect,
    onDayHoursChange,
    weekLockEntries,
    holidays,
    checkRowDisabled,
    checkRowHideActions,
    isCategorySelectionDisabled,
    onProjectCategorySelected,
    currentWeekDateRange,
  } = props;

  const getWholeWeekLocked = () => {
    return weekLockEntries && Object.entries(weekLockEntries).every(([day, val]) => val);
  };

  const canCopyPreviousWeek = () => {
    const weekHasLocks = weekLockEntries && Object.entries(weekLockEntries).some(([day, val]) => val);
    return props.isCurrentWeekEmpty && !weekHasLocks;
  };

  const getTableRowFields = () => {
    const rowfields = {
      type: {
        label: "",
        type: FormEnums.FormFieldType.CUSTOM,
        customField: <IconField></IconField>,
      },
      name: {
        label: "",
        controlLabel: tableHeaderValues?.name,
        type: FormEnums.FormFieldType.CUSTOM,
        customField: (
          <ProjectCategoryPickerField
            getAvailableCategories={availableCategories}
            isCategorySelectionDisabled={isCategorySelectionDisabled}
            onItemSelected={onProjectCategorySelected}
          />
        ),
        widthColumnCount: 2,
        icon: IconDefinitionConstants.WCalendarTime,
        classes: { formControl: cssExports.titleText },
      },
      childName: {
        label: " ",
        placeholder: TranslationKeys.TimeReporting.Child,
        controlLabel: "",
        type: FormEnums.FormFieldType.TEXT,
        readonly: (values: TimeReportingFormTypes.WeeklyTimeReportRow) =>
          !(
            values.deviationType === WintSuperkollAPIModel.SalaryDeviationType.ParentalLeave ||
            values.deviationType === WintSuperkollAPIModel.SalaryDeviationType.TenDays
          ),
        widthColumnCount: 2,
      },
      commentsCount: {
        label: " ",
        controlLabel: " ",
        type: FormEnums.FormFieldType.CUSTOM,
        widthColumnCount: 1,
        rightAlign: true,
        customField: (
          <CommentsCountField
            tableHeaderValues={tableHeaderValues}
            weekLockEntries={weekLockEntries}
            currentWeekDateRange={currentWeekDateRange}
          />
        ),
        hidden: (values: TimeReportingFormTypes.WeeklyTimeReportRow) =>
          values.name === LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Total),
      },
    } as FormFieldDefinitionType<TimeReportingFormTypes.WeeklyTimeReportRow>;

    DateConstants.FullWeekStartWithMonday.forEach((day, index) => {
      (rowfields as any)[day] = {
        label: "-",
        controlLabel: `${LocalizationHelperInstance.getFormattedMessage(
          `TimeReporting.WeekDayShortFromMonday_${index}`
        )}. ${(tableHeaderValues as any)[day]}`,
        type: FormEnums.FormFieldType.NUMBER,
        rightAlign: true,
        extraProps: { suffix: "h", decimalPoints: 2 },
        disabled: (values: TimeReportingFormTypes.WeeklyTimeReportRow) =>
          (weekLockEntries && (weekLockEntries as any)[day]) || (values.weekLocks && (values.weekLocks as any)[day]),
        onChangeHandler: onDayHoursChange,
      };
    });
    return rowfields;
  };

  return (
    <GenericForm<TimeReportingFormTypes.WeeklyTimeReport>
      formRef={ref}
      {...props}
      fields={{
        rows: {
          label: TranslationKeys.Settings.Units,
          controlLabel: "",
          type: FormEnums.FormFieldType.COMPACTEDITABLETABLE,
          innerFields: getTableRowFields(),
          disabled: getWholeWeekLocked(),
          extraProps: {
            headerRowFieldName: FormFieldConstants.TimeReporting.WeeklyTimeReport.HeaderRow,
            activateLastRow: true,
            rowDisabled: checkRowDisabled,
            rowHideActions: checkRowHideActions,
            columnSpanActions: (values?: TimeReportingFormTypes.WeeklyTimeReportRow, columnName?: string) =>
              columnName === FormFieldConstants.TimeReporting.WeeklyTimeReportRow.Name &&
              values?.type === TimeReportType.project
                ? 2
                : undefined,
            cellHideActions: (values?: TimeReportingFormTypes.WeeklyTimeReportRow, columnName?: string) =>
              columnName === FormFieldConstants.TimeReporting.WeeklyTimeReportRow.ChildName &&
              values?.type === TimeReportType.project,
            externalHeader: (
              <WeeklyFormTableHeader
                innerFields={getTableRowFields()}
                weekLocks={weekLockEntries}
                holidays={holidays}
              ></WeeklyFormTableHeader>
            ),
            onItemRemove: props.onRowRemove,
          } as CompactEditableTableFieldProps<TimeReportingFormTypes.WeeklyTimeReportRow>,
        },
        timeReportSuggestions: {
          label: "",
          type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
          widthColumnCount: 12,
          innerFields: {
            projectSuggestions: {
              label: TranslationKeys.TimeReporting.CreateNewProject,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                items: availableProjects,
                onItemSelect: onProjectSelect,
              } as AutoCompleteExtraProps<TimeReportingFormTypes.WeeklyTimeReportRow>,
              classes: { formField: cssExports.compactField },
              // 5 comes closest to the end of the project/category column in the rows above (4 falls short:
              // that column sits behind a fixed-width icon column). While the week has no rows there is no
              // column to match, and Kopiera föregående vecka needs 6 to stay on one line on narrow screens.
              widthColumnCount: canCopyPreviousWeek() ? 3 : 5,
              disabled: getWholeWeekLocked(),
            },
            deviationSuggestions: {
              label: TranslationKeys.SalaryDeviation.AddTimeDeviation,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                items: availableDeviations,
                onItemSelect: onDeviationSelect,
              } as AutoCompleteExtraProps<TimeReportingFormTypes.WeeklyTimeReportRow>,
              classes: { formField: props.salaryDeviationActive ? cssExports.compactField : cssExports.hidden },
              widthColumnCount: 3,
              disabled: getWholeWeekLocked(),
            },
            copyPreviousWeek: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: (
                <MoreActionsButton
                  size="sm"
                  alignItems="flex-end"
                  menuPlacement="bottom"
                  onClick={() => props.onCopyLastWeek?.(true)}
                  menuItems={[
                    {
                      description: LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.TimeReporting.CopyPreviousWeekWithoutHours
                      ),
                      icon: IconDefinitionConstants.WCopy,
                      onClickHandler: () => {
                        props.onCopyLastWeek?.(false);
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.CopyPreviousWeek)}
                </MoreActionsButton>
              ),
              widthColumnCount: canCopyPreviousWeek() ? 6 : 4,
              classes: { formField: canCopyPreviousWeek() ? cssExports.compactField : cssExports.hidden },
            },
          },
        },
      }}
      inline={false}
      hideOptionalIndicator
    />
  );
};

export default forwardRef(WeeklyForm);
